import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { closestCenter, DndContext, DragOverlay, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { WidgetListContext } from '../widget/list';
import { OverlayContainer } from '.';
import { DEFAULT_CONTEXT_VALUE } from './constants';
import { SortableItem } from './sortable-item';
import { createItemIdMaps, findItem, findItemIndex, getListIdFromDroppableId, getOrCreateItemId, isListDroppableId } from './utils';
export { createItemIdMaps, getListDroppableId, getOrCreateItemId, getOrCreateObjectItemId, reorderItems } from './utils';
const DragListContext = React.createContext(DEFAULT_CONTEXT_VALUE);
export const useDragListContext = () => React.useContext(DragListContext);
export const DragListProvider = ({ children, onSave: providerOnSave }) => {
    const [activeId, setActiveId] = React.useState(null);
    const registrationsRef = React.useRef(new Map());
    const itemIdMapsRef = React.useRef(createItemIdMaps());
    const idCounterRef = React.useRef(0);
    const getItemId = React.useCallback((item) => getOrCreateItemId(item, itemIdMapsRef.current, idCounterRef, 'provider-item'), []);
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
    }));
    const registerList = React.useCallback((registration) => {
        registrationsRef.current.set(registration.listId, registration);
    }, []);
    const unregisterList = React.useCallback((listId) => {
        registrationsRef.current.delete(listId);
    }, []);
    const getRegistrations = () => Array.from(registrationsRef.current.values());
    const findListByItemId = (itemId) => getRegistrations().find((registration) => registration.items.some((item) => getItemId(item) === itemId));
    const findListById = (listId) => registrationsRef.current.get(listId);
    const findTargetList = (overId) => {
        const overIdStr = String(overId);
        if (isListDroppableId(overIdStr)) {
            return findListById(getListIdFromDroppableId(overIdStr));
        }
        return findListByItemId(overId);
    };
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };
    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over) {
            return;
        }
        const activeList = findListByItemId(active.id);
        const overList = findTargetList(over.id);
        if (!activeList || !overList || activeList.listId === overList.listId) {
            return;
        }
        const activeItem = findItem(activeList, active.id, getItemId);
        if (!activeItem) {
            return;
        }
        const overIdStr = String(over.id);
        const isOverEmptyList = isListDroppableId(overIdStr);
        const overIndex = isOverEmptyList
            ? -1
            : findItemIndex(overList, over.id, getItemId);
        const newActiveItems = activeList.items.filter((item) => getItemId(item) !== active.id);
        activeList.onReorder?.(newActiveItems);
        const insertIndex = overIndex >= 0 ? overIndex : overList.items.length;
        const newOverItems = [
            ...overList.items.slice(0, insertIndex),
            activeItem,
            ...overList.items.slice(insertIndex)
        ];
        overList.onReorder?.(newOverItems);
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const activeList = findListByItemId(active.id);
            const overList = findListByItemId(over.id);
            if (activeList &&
                overList &&
                activeList.listId === overList.listId) {
                const oldIndex = findItemIndex(activeList, active.id, getItemId);
                const newIndex = findItemIndex(activeList, over.id, getItemId);
                if (oldIndex !== -1 && newIndex !== -1) {
                    const reorderedItems = arrayMove(activeList.items, oldIndex, newIndex);
                    activeList.onReorder?.(reorderedItems);
                }
            }
        }
        if (activeId !== null && providerOnSave) {
            void Promise.resolve(providerOnSave());
        }
        setActiveId(null);
    };
    const getActiveItemWithMeta = () => {
        if (!activeId) {
            return undefined;
        }
        const registration = getRegistrations().find((reg) => reg.items.some((item) => getItemId(item) === activeId));
        if (!registration) {
            return undefined;
        }
        const item = findItem(registration, activeId, getItemId);
        if (!item) {
            return undefined;
        }
        return {
            item,
            id: String(activeId),
            listId: registration.listId
        };
    };
    const activeItemWithMeta = getActiveItemWithMeta();
    const activeListRegistration = activeItemWithMeta
        ? registrationsRef.current.get(activeItemWithMeta.listId)
        : undefined;
    const contextValue = React.useMemo(() => ({
        isInProvider: true,
        registerList,
        unregisterList,
        activeId,
        getItemId,
        onSave: providerOnSave
    }), [registerList, unregisterList, activeId, getItemId, providerOnSave]);
    const overlayContent = activeItemWithMeta && activeListRegistration ? (_jsx(OverlayContainer, { children: _jsx(SortableItem, { id: activeItemWithMeta.id, item: activeItemWithMeta.item, isOverlay: true, renderItem: activeListRegistration.renderItem, dragHandleAriaLabel: activeListRegistration.dragHandleAriaLabel, withoutDivider: activeListRegistration.withoutDivider, withoutHandle: activeListRegistration.withoutHandle }) })) : null;
    const wrappedOverlayContent = overlayContent && activeListRegistration?.widgetListContext ? (_jsx(WidgetListContext.Provider, { value: activeListRegistration.widgetListContext, children: overlayContent })) : (overlayContent);
    return (_jsx(DragListContext.Provider, { value: contextValue, children: _jsxs(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragEnd: handleDragEnd, children: [children, createPortal(_jsx(DragOverlay, { children: wrappedOverlayContent }), document.body)] }) }));
};
